import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';

import MapaDirectorio from './MapaDirectorio';
import { PUESTOS_DIRECTORIO } from './directorioPuestosData';

const normalizar = (s) =>
    String(s == null ? '' : s)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, ''); // quita tildes/diacríticos para búsqueda

const DirectorioPuestos = () => {
    const puntos = PUESTOS_DIRECTORIO;

    const clientesOpciones = useMemo(() => {
        const conteo = {};
        const color = {};
        puntos.forEach((p) => {
            conteo[p.cliente] = (conteo[p.cliente] || 0) + 1;
            color[p.cliente] = p.color;
        });
        return Object.keys(conteo)
            .sort()
            .map((c) => ({ label: `${c} (${conteo[c]})`, value: c, color: color[c] }));
    }, [puntos]);

    const [clientesSel, setClientesSel] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [selNonce, setSelNonce] = useState(0);
    const [hoveredId, setHoveredId] = useState(null);

    const filtrados = useMemo(() => {
        const q = normalizar(busqueda.trim());
        return puntos.filter((p) => {
            if (clientesSel.length > 0 && !clientesSel.includes(p.cliente)) return false;
            if (!q) return true;
            const heno = normalizar(
                [p.puesto, p.cliente, p.grupo, p.direccion, p.referencia, p.responsable].join(' ')
            );
            return heno.includes(q);
        });
    }, [puntos, clientesSel, busqueda]);

    const verEnMapa = (row) => {
        setSelectedId(row.id);
        setSelNonce((n) => n + 1);
    };

    // --- Templates de columnas ---
    const puestoBody = (row) => (
        <div>
            <span style={{ fontWeight: 600, color: '#1e293b' }}>{row.puesto}</span>
            {row.grupo && row.grupo !== row.cliente && (
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{row.grupo}</div>
            )}
        </div>
    );

    const clienteBody = (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: row.color, display: 'inline-block' }} />
            <span style={{ fontSize: '12px' }}>{row.cliente}</span>
        </span>
    );

    const direccionBody = (row) => (
        <span title={row.direccion} style={{ display: 'block', maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '12px', color: '#475569' }}>
            {row.direccion}
        </span>
    );

    const accionBody = (row) => (
        <Button
            icon="pi pi-map-marker"
            className="p-button-rounded p-button-text p-button-sm"
            tooltip="Ver en el mapa"
            tooltipOptions={{ position: 'left' }}
            onClick={(e) => {
                e.stopPropagation();
                verEnMapa(row);
            }}
        />
    );

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
            <span className="p-input-icon-left w-full md:w-20rem">
                <i className="pi pi-search" />
                <InputText
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar puesto, dirección, responsable..."
                    className="w-full"
                />
            </span>
            <MultiSelect
                value={clientesSel}
                options={clientesOpciones}
                onChange={(e) => setClientesSel(e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder="Filtrar por cliente"
                display="chip"
                showClear
                className="w-full md:w-20rem"
            />
        </div>
    );

    return (
        <div className="surface-card p-4 shadow-3 border-round-xl">
            <div className="flex align-items-center justify-content-between mb-3">
                <div>
                    <h4 className="m-0 text-900">Directorio de Puestos</h4>
                    <span className="text-600 text-sm">Índice y ubicación geográfica de puestos por cliente</span>
                </div>
                <span className="text-600 text-sm">
                    <i className="pi pi-map-marker mr-1" />
                    {filtrados.length} de {puntos.length} puestos
                </span>
            </div>

            <div className="grid">
                {/* Índice buscable */}
                <div className="col-12 lg:col-5">
                    <DataTable
                        value={filtrados}
                        dataKey="id"
                        size="small"
                        header={header}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 30, 56]}
                        scrollable
                        scrollHeight="520px"
                        selectionMode="single"
                        selection={filtrados.find((f) => f.id === selectedId) || null}
                        onSelectionChange={(e) => e.value && verEnMapa(e.value)}
                        onRowMouseEnter={(e) => setHoveredId(e.data.id)}
                        onRowMouseLeave={() => setHoveredId(null)}
                        emptyMessage="No se encontraron puestos."
                        rowHover
                    >
                        <Column header="Puesto" body={puestoBody} sortable sortField="puesto" />
                        <Column header="Cliente" body={clienteBody} sortable sortField="cliente" />
                        <Column header="Dirección" body={direccionBody} />
                        <Column header="Responsable" field="responsable" style={{ fontSize: '12px' }} />
                        <Column body={accionBody} style={{ width: '3rem' }} />
                    </DataTable>
                </div>

                {/* Mapa */}
                <div className="col-12 lg:col-7">
                    <MapaDirectorio
                        listaDePuntos={filtrados.map((p) => ({ ...p, nombre: p.puesto }))}
                        selectedPuntoId={selectedId}
                        selNonce={selNonce}
                        hoveredPuntoId={hoveredId}
                    />
                    <div className="flex flex-wrap gap-3 mt-2 px-1">
                        {clientesOpciones.map((c) => (
                            <span key={c.value} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#475569' }}>
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.color, display: 'inline-block' }} />
                                {c.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DirectorioPuestos;
